import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { ErrorMessage } from '@/components/common/ErrorMessage'
import { DeviceStatus } from '@/components/device/DeviceStatus'
import { useUser, useUserLinkedAccounts, useSetDefaultAccount, useToggleUserAdmin } from '@/hooks/useUsers'
import { useUserDevices } from '@/hooks/useDevices'
import { useAuthStore } from '@/stores/authStore'
import { adminService } from '@/services/admin.service'
import { mapUserFromApi } from '@/utils/userMapper'
import { ArrowLeft, Edit, KeyRound, Mail, Phone, Star, UserCheck } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/components/ui/use-toast'
import { userService } from '@/services/user.service'
import { format, formatDistanceToNow } from 'date-fns'
import type { UserDevice } from '@/types/user.types'
import type { DeviceStatus as DeviceStatusType } from '@/types/device.types'

export const UserDetailPage = () => {
  const { userId } = useParams<{ userId: string }>()
  const { data: user, isLoading, error } = useUser(userId!)
  const { data: linkedAccounts, isLoading: accountsLoading, error: accountsError } = useUserLinkedAccounts(userId!)
  const { data: userDevices, isLoading: devicesLoading, error: devicesError } = useUserDevices(userId!)
  const setDefaultAccount = useSetDefaultAccount()
  const toggleUserAdmin = useToggleUserAdmin()
  const currentUser = useAuthStore((s) => s.user)
  const startImpersonation = useAuthStore((s) => s.startImpersonation)
  const isSuperUser = currentUser?.isSuperUser ?? false
  const { toast } = useToast()
  const navigate = useNavigate()
  const [impersonating, setImpersonating] = useState(false)

  const handleImpersonate = async () => {
    setImpersonating(true)
    try {
      const { token, user: apiUser } = await adminService.impersonateUser(userId!)
      const mappedUser = mapUserFromApi(apiUser)
      startImpersonation(mappedUser, token)
      localStorage.setItem('auth_token', token)
      navigate('/dashboard')
    } catch {
      toast({ title: 'Error', description: 'Failed to impersonate user', variant: 'destructive' })
    } finally {
      setImpersonating(false)
    }
  }

  const handlePasswordReset = async () => {
    try {
      await userService.sendPasswordReset(userId!)
      toast({ title: 'Success', description: `Password reset email sent to ${user?.email}` })
    } catch {
      toast({ title: 'Error', description: 'Failed to send password reset email', variant: 'destructive' })
    }
  }

  if (isLoading) return <LoadingSpinner />
  if (error || !user) return <ErrorMessage message="Failed to load user details." />

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/users">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {user.displayName || `${user.firstName} ${user.lastName}`}
            </h1>
            <p className="text-muted-foreground">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSuperUser && (
            <>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" disabled={impersonating}>
                    <UserCheck className="mr-2 h-4 w-4" />
                    Impersonate
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Impersonate User</AlertDialogTitle>
                    <AlertDialogDescription>
                      You are about to log in as{' '}
                      <strong>{user.firstName} {user.lastName} ({user.email})</strong>.
                      You will see the portal as this user does. Use the banner at the top to stop impersonating.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleImpersonate}>
                      Impersonate
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline">
                    <KeyRound className="mr-2 h-4 w-4" />
                    Send Password Reset
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Send Password Reset</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to send a password reset email to{' '}
                      <strong>{user.email}</strong>?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handlePasswordReset}>
                      Send Reset Email
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          <Button variant="outline" asChild>
            <Link to={`/users/${userId}/edit`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
        </div>
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="accounts">Linked Accounts</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
        </TabsList>

        <TabsContent value="details">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span>{user.email}</span>
                </div>
                {user.phoneNumber && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{user.phoneNumber}</span>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Contact Preference</p>
                  <Badge variant="outline" className="mt-1">
                    {user.contactPref}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge variant={user.isActive ? 'success' : 'secondary'} className="mt-1">
                    {user.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Login</p>
                  <p className="mt-1">
                    {user.lastLogin ? format(new Date(user.lastLogin), 'PPP p') : 'Never'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Joined</p>
                  <p className="mt-1">{format(new Date(user.createdAt), 'PPP')}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="accounts">
          <Card>
            <CardHeader>
              <CardTitle>Linked Accounts</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {accountsError ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-destructive">
                    Failed to load linked accounts. Please try refreshing.
                  </p>
                </div>
              ) : accountsLoading ? (
                <div className="p-6">
                  <LoadingSpinner />
                </div>
              ) : linkedAccounts && linkedAccounts.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Default</TableHead>
                      <TableHead>Admin</TableHead>
                      {isSuperUser && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedAccounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell className="font-medium">
                          <Link to={`/accounts/${account.id}`} className="hover:underline">
                            {account.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {account.isDefault ? (
                            <Badge variant="success">
                              <Star className="mr-1 h-3 w-3" />
                              Default
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={account.isAdmin ? 'default' : 'secondary'}>
                            {account.isAdmin ? 'Yes' : 'No'}
                          </Badge>
                        </TableCell>
                        {isSuperUser && (
                          <TableCell className="text-right space-x-2">
                            {!account.isDefault && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={setDefaultAccount.isPending}
                                onClick={() =>
                                  setDefaultAccount.mutate({
                                    accountId: account.id,
                                    userId: userId!,
                                  })
                                }
                              >
                                Set Default
                              </Button>
                            )}
                            <div className="inline-flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">Admin</span>
                              <Switch
                                checked={account.isAdmin}
                                disabled={toggleUserAdmin.isPending}
                                onCheckedChange={(checked) =>
                                  toggleUserAdmin.mutate({
                                    accountId: account.id,
                                    userId: userId!,
                                    isAdmin: checked,
                                  })
                                }
                              />
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  No linked accounts found.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="devices">
          <Card>
            <CardHeader>
              <CardTitle>Accessible Devices</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {devicesError ? (
                <div className="p-6 text-center">
                  <p className="text-sm text-destructive">
                    Failed to load devices. Please try refreshing.
                  </p>
                </div>
              ) : devicesLoading ? (
                <div className="p-6">
                  <LoadingSpinner />
                </div>
              ) : userDevices && userDevices.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Serial Number</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Battery</TableHead>
                        <TableHead>Last Seen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userDevices.map((device: UserDevice) => (
                        <TableRow key={device.id}>
                          <TableCell className="font-medium">
                            <Link to={`/devices/${device.id}`} className="hover:underline">
                              {device.name}
                            </Link>
                          </TableCell>
                          <TableCell>{device.serialNumber}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{device.deviceType}</Badge>
                          </TableCell>
                          <TableCell>
                            <DeviceStatus status={device.status as DeviceStatusType} />
                          </TableCell>
                          <TableCell>
                            {device.batteryLevel ? `${device.batteryLevel}%` : '-'}
                          </TableCell>
                          <TableCell>
                            {device.lastSeen ? (
                              <span
                                title={format(
                                  new Date(device.lastSeen),
                                  'MMM d, yyyy HH:mm:ss'
                                )}
                              >
                                {formatDistanceToNow(new Date(device.lastSeen), {
                                  addSuffix: true,
                                })}
                              </span>
                            ) : (
                              'Never'
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-6 text-center text-muted-foreground">
                  No devices found for this user.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
